import React, { Fragment } from 'react';
import { connect, DispatchProp } from 'react-redux';
import { createSelector } from 'reselect';
import { difference, noop } from 'lodash';
import cn from 'classnames';
import cd from 'debug';
import { compose } from 'redux';
import { Arg1 } from 'tsargs';
import core, { View, App, Viewport, GQL } from '@gin-betting/core';

import { SELECTOR_STUB, LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import {
    MiddlePartMatchRow,
    MarketsCount,
    StreamLink,
    SmartLink,
    MatchDateTime,
    TeamsTitle,
    TournamentName,
} from 'components';
import type { ParentPath, State as AppState } from 'types';
import {
    withStreamModule,
    WithStreamModuleTypes,
    withStreamWidgetSettings,
    WithStreamWidgetSettingsTypes,
} from 'hoc';
import {
    checkIfNewsPage,
    topMarketsVerify,
    checkIsSportEventViewable,
    checkIsMatchTrackerDisabled,
} from 'utils';
import {
    selectIsSportEventViewable,
    selectBetradarId,
    selectRouteBySportEvent,
} from 'selectors';
import { AsyncOddButton } from 'modules/betslip/async';
import { AsyncStream } from 'modules/stream/async';
import favorite from 'modules/favorite';
import { getTopMarketComponentsBySport } from '../../getTopMarkets';
import {
    selectIsIncludeUserMarkets,
    selectTopMarketsByMatchId,
} from '../../selectors';
import Score from './Score';
import { TopMarketsContainer } from './Markets';
import StreamMarketCount from './StreamMarketCount';
import { SelectIsOpenContext } from './SelectIsOpenContext';
import './sportEventRow.scss';

const { getDefaultVariables } = core.utils;
const { withSubscription, makePure } = core.hocs;
const {
    selectMatchCompetitorsValid,
    makeMatchSelector,
    makeTournamentSelector,
    selectPreviewTournamentIdFromMatch,
    selectMobileMatchStream,
} = core.entities.selectors;
const { makeViewportSelector } = core.viewport.selectors;
const { ToggleFavoriteButton } = favorite.components;
const { isHCP, isTotal } = topMarketsVerify;

const debug = cd('match-list:components:MatchRowContainer');

const matchPropsToPick = [
    'title' as const,
    'id' as const,
    'marketsCount' as const,
    'sportId' as const,
    'streams' as const,
    'liveCoverage' as const,
    'status' as const,
    'startTime' as const,
    'tournament' as const,
    'score' as const,
    'slug' as const,
    'hasMatchLog' as const,
];

const tournamentPropsToPick = [
    'id' as const,
    'name' as const,
    'masterId' as const,
    'slug' as const,
];

type Match = Pick<View.Match, typeof matchPropsToPick[number]>;

type Tournament = Pick<View.Tournament, typeof tournamentPropsToPick[number]>;

interface MappedProps {
    match: Match | null;
    tournament: Tournament | null;
    competitorsValid: View.CompetitorsValid;
    lessThanOrEqual: Viewport.Breakpoints;
    hasHeader: boolean;
    topMarketsCount: number;
    streamUrl: ReturnType<typeof selectMobileMatchStream>;
    markets: ReturnType<typeof selectTopMarketsByMatchId>;
    isIncludeUserMarkets: ReturnType<typeof selectIsIncludeUserMarkets>;
    isStreamOpen: boolean | null;
    isOneMarketView: boolean;
    count: number;
    scoreCount: number;
    hasValidStream: boolean;
    hasValidBetradarId: boolean;
    betradarId: string | null;
    route: ParentPath;
}

interface MappedActions {
    addMatchId:
        | WithStreamModuleTypes.StreamModule['actions']['addMatchId']
        | VoidFn;
    deleteMatchId:
        | WithStreamModuleTypes.StreamModule['actions']['deleteMatchId']
        | VoidFn;
}

interface OwnProps {
    matchId: string;
    prevMatchId: string | undefined;
    isResultPage?: boolean;
}

interface ConnectOwnProps
    extends OwnProps,
        WithStreamModuleTypes.WithStreamModule {}

type Props = OwnProps &
    MappedProps &
    MappedActions &
    WithStreamModuleTypes.WithStreamModule &
    WithStreamWidgetSettingsTypes;
//  & ResponsiveComponents.ResponseContainerProps;

type State = {
    isSelectOpen: boolean;
    isOneMarketView: boolean;
};

const getStreamSettings = ({
    hasValidStream,
    hasValidBetradarId,
    isMatchTrackerDisabled,
    isSmall,
    isStreamOpen,
}: {
    hasValidStream: boolean;
    hasValidBetradarId: boolean;
    isMatchTrackerDisabled: boolean;
    isSmall: boolean;
    isStreamOpen: boolean | null;
}) => {
    const isStreamAvailable =
        (hasValidStream || (hasValidBetradarId && !isMatchTrackerDisabled)) &&
        !isSmall;

    const isViewerOpen = !!isStreamOpen && isStreamAvailable;

    return {
        isViewerOpen,
        isStreamAvailable,
    };
};

export class SportEventRow extends React.Component<Props, State> {
    state: State = {
        isSelectOpen: false,
        isOneMarketView: true,
    };

    constructor(props: Props) {
        super(props);
        const { isOneMarketView } = props;

        this.state.isOneMarketView = isOneMarketView;
    }

    componentDidUpdate() {
        const { isOneMarketView } = this.state;
        const { isOneMarketView: isOneMarketViewProps } = this.props;

        if (isOneMarketView && !isOneMarketViewProps) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                isOneMarketView: false,
            });
        }
    }

    getLinkToMatch = (): string | null => {
        const { isResultPage, match, route } = this.props;

        return match
            ? `${route}${
                  isResultPage ? App.Routes.RESULT_MATCH : App.Routes.MATCH
              }${match.slug}`
            : null;
    };

    setIsSelectOpen = (value: boolean): void =>
        this.setState({ isSelectOpen: value });

    toggleStream = (): void => {
        const {
            matchId,
            hasValidStream,
            hasValidBetradarId,
            isStreamOpen,
            lessThanOrEqual,
            disableMatchTracker,
            match,
        } = this.props;

        const isSmall = lessThanOrEqual.small;

        const { isViewerOpen } = getStreamSettings({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled: checkIsMatchTrackerDisabled(
                disableMatchTracker,
                match?.sportId
            ),
            isSmall,
            isStreamOpen,
        });

        if (isViewerOpen) {
            this.props.deleteMatchId({ matchId });
        } else {
            this.props.addMatchId({ matchId, withClearPrev: true });
        }
    };

    renderMiddle = (): JSX.Element | null => {
        const { isOneMarketView } = this.state;
        const {
            match,
            hasHeader,
            competitorsValid,
            count,
            scoreCount,
        } = this.props;

        if (!match) return null;

        const { id: matchId, sportId, status } = match;
        const isEnded =
            status === GQL.SportEventStatus.ENDED ||
            status === GQL.SportEventStatus.CLOSED;

        const showScores =
            [...LIVE_SPORTEVENT_STATUSES, GQL.SportEventStatus.ENDED].includes(
                status
            ) && !checkIfNewsPage();

        const { awayLogo, awayName, homeLogo, homeName } = competitorsValid;
        const linkToMatch = this.getLinkToMatch();

        if (isOneMarketView) {
            return (
                <MiddlePartMatchRow
                    sportId={sportId}
                    linkToMatch={linkToMatch}
                    homeTitle={homeName}
                    awayTitle={awayName}
                    homeLogo={homeLogo}
                    awayLogo={awayLogo}
                >
                    <TopMarketsContainer
                        matchId={matchId}
                        hasMatchHeader={hasHeader}
                        sportId={sportId}
                        count={1}
                    />
                </MiddlePartMatchRow>
            );
        }

        return (
            <Fragment>
                <div styleName="teamsTitle">
                    <TeamsTitle
                        sportId={sportId}
                        competitorsValid={competitorsValid}
                        linkToMatch={linkToMatch}
                    />
                    {showScores && (
                        <Score
                            isEnded={isEnded}
                            hasMatchHeader={hasHeader}
                            numberOfShowed={scoreCount}
                            sportId={sportId}
                            matchId={matchId}
                        />
                    )}
                </div>
                <div styleName="markets">
                    <SelectIsOpenContext.Provider value={this.setIsSelectOpen}>
                        <TopMarketsContainer
                            matchId={matchId}
                            hasMatchHeader={hasHeader}
                            sportId={sportId}
                            count={count}
                        />
                    </SelectIsOpenContext.Provider>
                </div>
            </Fragment>
        );
    };

    renderFooter = (): JSX.Element | null => {
        const {
            match,
            matchId,
            hasValidStream,
            hasValidBetradarId,
            disableMatchTracker,
        } = this.props;

        if (!match) return null;

        const { marketsCount } = match;

        const isSportEventViewable = checkIsSportEventViewable({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled: checkIsMatchTrackerDisabled(
                disableMatchTracker,
                match.sportId
            ),
        });

        const linkToMatch = this.getLinkToMatch();

        return (
            <div styleName="footer">
                <ToggleFavoriteButton matchId={matchId} />

                <MarketsCount
                    marketsCount={marketsCount}
                    topMarketsCount={1}
                    linkToMatch={linkToMatch}
                />
                {isSportEventViewable && (
                    <div styleName="stream">
                        <StreamLink
                            linkToMatch={linkToMatch}
                            withTitle={false}
                        />
                    </div>
                )}
            </div>
        );
    };

    render() {
        debug('render', this.props);
        const { isOneMarketView } = this.state;
        const {
            hasValidStream,
            hasValidBetradarId,
            match,
            tournament,
            hasHeader,
            isResultPage,
            topMarketsCount,
            lessThanOrEqual,
            matchId,
            streamUrl,
            isStreamOpen,
            betradarId,
            route,
            disableMatchTracker,
        } = this.props;

        if (!match) return null;

        const isMatchTrackerDisabled = checkIsMatchTrackerDisabled(
            disableMatchTracker,
            match.sportId
        );

        const isSportEventViewable = checkIsSportEventViewable({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled,
        });

        const { isSelectOpen } = this.state;

        const {
            sportId,
            startTime,
            liveCoverage,
            status,
            marketsCount,
            title,
            slug,
            hasMatchLog,
        } = match;

        const isSmall = lessThanOrEqual.small;
        const isExtraSmall = lessThanOrEqual.extraSmall;
        const isNewsPage = checkIfNewsPage();

        const { isViewerOpen, isStreamAvailable } = getStreamSettings({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled,
            isSmall,
            isStreamOpen,
        });

        const isLive = LIVE_SPORTEVENT_STATUSES.includes(status);
        const linkToMatch = this.getLinkToMatch();

        const SportEventRowComponent = (
            <div
                styleName={cn('container', {
                    'is-one-market-view': isOneMarketView,
                    'is-stream-open': isViewerOpen,
                    'has-header': hasHeader,
                    'is-select-open': isSelectOpen,
                })}
            >
                {hasHeader && (
                    <div
                        styleName={cn('header', {
                            'has-valid-stream':
                                isSportEventViewable &&
                                (!isResultPage || isLive),
                        })}
                    >
                        {tournament && (
                            <TournamentName
                                sportId={sportId}
                                tournamentName={tournament.name}
                                masterId={tournament.masterId}
                                slug={tournament.slug}
                                isDisabled={isNewsPage}
                                route={route}
                            />
                        )}

                        {__DEV__ && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: 'darkorange',
                                    fontSize: 12,
                                    fontWeight: 700,
                                }}
                            >
                                {matchId}
                            </span>
                        )}
                    </div>
                )}
                <div
                    styleName={cn('body', {
                        hasHeader,
                        'is-select-open': isSelectOpen,
                    })}
                >
                    {!isExtraSmall && (
                        <ToggleFavoriteButton matchId={matchId} />
                    )}

                    <MatchDateTime
                        matchId={matchId}
                        isLive={isLive}
                        startTime={startTime}
                        liveCoverage={liveCoverage}
                        isSmall={isSmall}
                        sportId={sportId}
                    />

                    {this.renderMiddle()}

                    {!isExtraSmall && (
                        <React.Fragment>
                            <StreamMarketCount
                                isResultPage={isResultPage}
                                linkToMatch={linkToMatch}
                                marketsCount={marketsCount}
                                topMarketsCount={
                                    isOneMarketView ? 1 : topMarketsCount
                                }
                                streamWithTitle={!isSmall}
                                toggleStream={
                                    isStreamAvailable
                                        ? this.toggleStream
                                        : undefined
                                }
                                isViewerOpen={isViewerOpen}
                                isLive={isLive}
                                isSportEventViewable={isSportEventViewable}
                            />
                        </React.Fragment>
                    )}
                </div>
                {isExtraSmall && this.renderFooter()}
                {isViewerOpen && streamUrl && (
                    <div styleName="player">
                        <AsyncStream
                            betradarId={betradarId}
                            sportId={sportId}
                            withPromoMatch={!isSmall}
                            isHeaderAtTop={true}
                            url={streamUrl}
                            matchId={matchId}
                            title={title}
                            OddButton={AsyncOddButton}
                            hasMatchLog={hasMatchLog}
                        />
                    </div>
                )}
            </div>
        );

        if (isNewsPage) {
            return (
                <SmartLink to={`${route}${App.Routes.MATCH}${slug}`}>
                    {SportEventRowComponent}
                </SmartLink>
            );
        }

        return SportEventRowComponent;
    }
}

const makeMapStateToProps = (
    state: AppState,
    ownProps: ConnectOwnProps
): MappedProps => {
    const selectMatch = makeMatchSelector({
        propsToPick: matchPropsToPick,
    });
    const selectTournamentIdByPrevMatch = makeMatchSelector({
        propsToPick: ['tournament'],
        selectMatchId: (_, props: { prevMatchId: string }) => props.prevMatchId,
    });

    const selectHasHeader = createSelector(
        selectMatch,
        selectTournamentIdByPrevMatch,
        (match, prevMatch) =>
            !(match && prevMatch && match.tournament === prevMatch.tournament)
    );

    const selectTournament = makeTournamentSelector({
        propsToPick: tournamentPropsToPick,
        selectTournamentId: selectPreviewTournamentIdFromMatch,
    });

    const selectLessThanOrEqual = makeViewportSelector('lessThanOrEqual');

    const selectIsMatchIdWithOpenedStream =
        ownProps.streamModule?.selectors.selectIsMatchIdWithOpenedStream ??
        SELECTOR_STUB;

    const sportEvent = selectMatch(state, ownProps);
    const tournament = selectTournament(state, ownProps);
    const lessThanOrEqual = selectLessThanOrEqual(state, ownProps);
    const streamUrl = selectMobileMatchStream(state, ownProps);
    const competitorsValid = selectMatchCompetitorsValid(state, ownProps);
    const isStreamOpen = selectIsMatchIdWithOpenedStream(state, ownProps);

    let hasHeader = selectHasHeader(state, ownProps);

    const markets = selectTopMarketsByMatchId(state, {
        matchId: ownProps.matchId,
    });

    const isIncludeUserMarkets = selectIsIncludeUserMarkets(state, { markets });
    const isGreat = lessThanOrEqual.great;
    const isSmall = lessThanOrEqual.small;
    const isExtraSmall = lessThanOrEqual.extraSmall;

    const { hasValidStream, hasValidBetradarId } = selectIsSportEventViewable(
        state,
        {
            matchId: ownProps.matchId,
        }
    );

    const count = isSmall ? 2 : 3;
    const scoreCount = isGreat ? (isSmall ? 1 : 2) : 3;

    const isExistHcpOrTotal = getIsExistHcpOrTotal(markets);

    const isOneMarketView =
        !!sportEvent &&
        (getTopMarketComponentsBySport(sportEvent.sportId, { count }).length ===
            1 ||
            !isExistHcpOrTotal) &&
        !isExtraSmall;

    if (!hasHeader) {
        const prevMarkets = selectTopMarketsByMatchId(state, {
            matchId: ownProps.prevMatchId,
        });

        const prevIsExistHcpOrTotal = getIsExistHcpOrTotal(prevMarkets);

        const marketsNames = markets.map(m => m.name);
        const prevMarketsNames = prevMarkets.map(m => m.name);

        hasHeader =
            difference(marketsNames, prevMarketsNames).length > 0 ||
            prevIsExistHcpOrTotal !== isExistHcpOrTotal;
    }

    const betradarId = selectBetradarId(state, {
        matchId: ownProps.matchId,
    });
    const route = selectRouteBySportEvent(state, {
        matchId: ownProps.matchId,
    });

    return {
        hasHeader,
        match: sportEvent,
        tournament,
        lessThanOrEqual,
        hasValidStream,
        hasValidBetradarId,
        isStreamOpen,
        competitorsValid,
        streamUrl,
        topMarketsCount: markets.length,
        markets,
        isIncludeUserMarkets,
        isOneMarketView,
        count,
        scoreCount,
        betradarId,
        route,
    };
};

const mapDispatchToProps = (
    dispatch: DispatchProp['dispatch'],
    { streamModule }: ConnectOwnProps
): MappedActions => {
    if (!streamModule)
        return {
            addMatchId: noop,
            deleteMatchId: noop,
        };

    const {
        actions: { addMatchId, deleteMatchId },
    } = streamModule;

    return {
        addMatchId: (payload: Arg1<typeof addMatchId>) =>
            dispatch(addMatchId(payload)),
        deleteMatchId: (payload: Arg1<typeof deleteMatchId>) =>
            dispatch(deleteMatchId(payload)),
    };
};

export default compose<React.ComponentClass<OwnProps>>(
    // withResponseContainer,
    withStreamModule,
    withStreamWidgetSettings,
    connect<MappedProps, MappedActions, ConnectOwnProps>(
        makeMapStateToProps,
        mapDispatchToProps
    ),
    withSubscription<
        MappedProps & MappedActions & ConnectOwnProps,
        'onUpdateSportEvent'
    >('matchList-MatchRow', 'onUpdateSportEvent', props => {
        const { markets: topMarkets, isIncludeUserMarkets } = props;

        if (isIncludeUserMarkets) {
            const topMarketTypesIds = topMarkets.map(market => market.typeId);

            return {
                sportEventId: props.matchId,
                marketTypes: topMarketTypesIds,
            };
        }

        const { marketStatuses } = getDefaultVariables();

        return {
            sportEventId: props.matchId,
            isTopMarkets: true,
            marketStatuses,
        };
    }),
    makePure({ maxDepth: 3 })
)(SportEventRow);

export const getIsExistHcpOrTotal = (markets: View.Market[]): boolean =>
    markets.some(m => isHCP(m) || isTotal(m));
